import { Box, Button, Flex, Heading, Link, Text } from '@chakra-ui/react';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { createArticleRelativeUrl } from '../common/libs/article';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import { createUserName } from '../common/libs/user';
import { ArticleSummaryImage, type ArticleHeader } from './ArticleSummary';

function ArticleRelatedSlidesPage({
  articles,
  isFullscreen,
}: {
  articles: ArticleHeader[];
  isFullscreen: boolean;
}) {
  const converted = articles
    .slice(0, 2) // 件数はここで調整するので上の階層では少なくとも2件以上取得できれば良い
    .map(
      ({
        articleId,
        topImageM,
        name,
        author,
        updateDate,
        discloseScope,
        status,
      }) => {
        const authorFullName = createUserName(author);
        const href = createArticleRelativeUrl(articleId);

        const summaryImage = (
          <ArticleSummaryImage
            articleId={articleId}
            status={status}
            topImageM={topImageM}
            updateDate={updateDate}
            alt={name}
          />
        );
        return {
          articleId,
          name,
          discloseScope,
          authorFullName,
          href,
          summaryImage,
        };
      },
    );

  return (
    <ArticleRelatedSlidesPageUi
      articles={converted}
      isFullscreen={isFullscreen}
      onClick={() => {
        sendAnalyticsEvent('slide', 'click', 'related_slide_button');
      }}
    />
  );
}

type ArticleRelatedSlidesPageUiProps = {
  articles: {
    articleId: string;
    name: string | null;
    authorFullName: string;
    href: string;
    summaryImage: React.ReactNode; // レスポンシブなNextImage
  }[];
  isFullscreen: boolean;
  onClick: () => void;
};

export function ArticleRelatedSlidesPageUi({
  articles,
  isFullscreen,
  onClick,
}: ArticleRelatedSlidesPageUiProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  // 縦横比が常に16:9になるように調整
  useEffect(() => {
    if (ref.current?.offsetWidth) {
      setHeight((ref.current.offsetWidth / 16) * 9);
    }
  }, [ref.current?.offsetWidth]);

  return (
    <Flex
      ref={ref}
      backgroundImage="url('/images/bg_article_related_slides.png')"
      backgroundSize="cover"
      w="full"
      h={isFullscreen ? height : 'full'}
      direction="column"
      position={isFullscreen ? 'absolute' : 'relative'}
    >
      <Flex
        p={{
          base: 3,
          sm: 5,
          md: isFullscreen ? 10 : undefined,
        }}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box color="brand.company">
          <small>
            <Text
              fontSize={{
                base: 'sm',
                sm: 'md',
                md: 'xl',
              }}
              fontWeight="normal"
            >
              関連テーマから出会おう。
            </Text>
          </small>
          <Text
            fontSize={{
              base: 'xl',
              sm: '2xl',
              md: '3xl',
              lg: isFullscreen ? '4xl' : undefined,
            }}
            mt="0.25em"
          >
            閲覧履歴からのおすすめ
          </Text>
        </Box>
        <Box
          flex={1}
          maxW={{
            base: 110,
            sm: 146,
          }}
        >
          <NextImage
            src={'/images/logo_slide.png'}
            width={492}
            height={100}
            alt="Antaa Slide"
          />
        </Box>
      </Flex>
      <Flex
        px={6}
        pb={5}
        alignItems="center"
        justify="center"
        gap={{
          base: '2em',
          md: '3em',
        }}
        flex={1}
      >
        {articles.map(
          ({ articleId, name, summaryImage, href, authorFullName }) => (
            <NextLink key={articleId} href={href} passHref>
              <Link
                color="inherit"
                _hover={{
                  textDecoration: 'none',
                }}
                onClick={onClick}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                h={'100%'}
                w={{
                  base: '40%',
                  md: '35%',
                }}
                textDecoration="none"
              >
                <Flex
                  flexDirection="column"
                  borderWidth={1}
                  rounded={4}
                  borderColor="black"
                  shadow="lg"
                  backgroundColor="white"
                  h={isFullscreen ? `${height * 0.45}px` : '100%'}
                  w={'100%'}
                >
                  <Box
                    height={`calc(100% - 31px)`}
                    position="relative"
                    backgroundColor="blackAlpha.300"
                  >
                    {summaryImage}
                  </Box>
                  <Box p={2}>
                    <Box>
                      <Text
                        h="2.5em"
                        fontSize={{
                          base: 'xs',
                          md: 'sm',
                          lg: isFullscreen ? 'lg' : undefined,
                        }}
                        fontWeight="bold"
                        noOfLines={2}
                        lineHeight="1.25"
                        maxW="100%"
                        wordBreak="break-all"
                      >
                        {name}
                      </Text>
                    </Box>
                    <Box
                      pt={2}
                      display={{
                        base: 'none',
                        md: 'block',
                      }}
                    >
                      <Text
                        fontSize={{
                          base: 'xs',
                          md: 'sm',
                          lg: isFullscreen ? 'md' : undefined,
                        }}
                        noOfLines={1}
                      >
                        {authorFullName}
                      </Text>
                    </Box>
                  </Box>
                </Flex>
                <Button
                  colorScheme="brand"
                  as="span"
                  shadow="lg"
                  size="sm"
                  rounded="full"
                  borderWidth={2}
                  borderColor="white"
                  mt={-1}
                  px={{
                    base: 5,
                    md: 8,
                  }}
                  py={2}
                  fontSize={{
                    base: 'xs',
                    md: 'sm',
                  }}
                >
                  続けて閲覧
                </Button>
              </Link>
            </NextLink>
          ),
        )}
      </Flex>
    </Flex>
  );
}

export default ArticleRelatedSlidesPage;
